/**
 * Cache utility for Firebase free tier optimization
 * Implements client-side caching to reduce Firestore reads
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface CacheConfig {
  CATEGORIES: number;      // 1 hour
  SHOPS: number;          // 30 minutes
  PRODUCTS: number;       // 5 minutes
  USER_PROFILE: number;   // 10 minutes
  PARTNERS: number;       // 15 minutes
  NOTIFICATIONS: number;  // 2 minutes
  // New TTL values for missing collections
  WISHLISTS: number;     // 5 minutes
  BOOKINGS: number;       // 2 minutes
  CLUB: number;          // 10 minutes
  ANALYTICS: number;     // 30 minutes
  SEARCH_RESULTS: number; // 1 minute
  DEFAULT: number;        // 1 minute
}

export const CACHE_TTL: CacheConfig = {
  CATEGORIES: 60 * 60 * 1000,        // 1 hour
  SHOPS: 30 * 60 * 1000,            // 30 minutes
  PRODUCTS: 5 * 60 * 1000,          // 5 minutes
  USER_PROFILE: 10 * 60 * 1000,     // 10 minutes
  PARTNERS: 15 * 60 * 1000,         // 15 minutes
  NOTIFICATIONS: 2 * 60 * 1000,     // 2 minutes
  // New TTL values for missing collections
  WISHLISTS: 5 * 60 * 1000,         // 5 minutes
  BOOKINGS: 2 * 60 * 1000,           // 2 minutes
  CLUB: 10 * 60 * 1000,              // 10 minutes
  ANALYTICS: 30 * 60 * 1000,         // 30 minutes
  SEARCH_RESULTS: 60 * 1000,          // 1 minute
  DEFAULT: 60 * 1000                // 1 minute
};

class CacheManager {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private maxSize: number = 1000;

  private getKey(prefix: string, identifier: string): string {
    return `${prefix}:${identifier}`;
  }

  private isExpired(entry: CacheEntry<unknown>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private cleanup(): void {
    if (this.cache.size > this.maxSize) {
      const sorted = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = Math.floor(this.maxSize * 0.2);
      for (let i = 0; i < toRemove; i++) {
        this.cache.delete(sorted[i][0]);
      }
    }

    // Remove expired entries
    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
      }
    }
  }

  set<T>(prefix: string, identifier: string, data: T, ttl: number = CACHE_TTL.DEFAULT): void {
    this.cleanup();
    
    const key = this.getKey(prefix, identifier);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get<T>(prefix: string, identifier: string): T | null {
    const key = this.getKey(prefix, identifier);
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) return null;

    if (this.isExpired(entry)) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  has(prefix: string, identifier: string): boolean {
    return this.get(prefix, identifier) !== null;
  }

  invalidate(prefix: string, identifier?: string): void {
    if (identifier) {
      const key = this.getKey(prefix, identifier);
      this.cache.delete(key);
    } else {
      // Invalidate all entries with this prefix
      for (const key of this.cache.keys()) {
        if (key.startsWith(`${prefix}:`)) {
          this.cache.delete(key);
        }
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }

  getStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

export const cacheManager = new CacheManager();

/**
 * Fetch data with caching
 * @param prefix - Cache prefix (e.g., 'products', 'shops')
 * @param identifier - Unique identifier for the data
 * @param fetcher - Function to fetch data if not cached
 * @param ttl - Time to live in milliseconds
 * @returns Promise<T>
 */
export async function fetchWithCache<T>(
  prefix: string,
  identifier: string,
  fetcher: () => Promise<T>,
  ttl: number = CACHE_TTL.DEFAULT
): Promise<T> {
  const cached = cacheManager.get<T>(prefix, identifier);
  
  if (cached !== null) {
    return cached;
  }

  const data = await fetcher();
  cacheManager.set(prefix, identifier, data, ttl);
  return data;
}

/**
 * Batch fetch with caching
 * @param items - Array of items to fetch
 * @param fetcher - Function to fetch multiple items
 * @param prefix - Cache prefix
 * @param ttl - Time to live
 * @returns Promise<T[]>
 */
export async function batchFetchWithCache<T extends { id: string }>(
  items: string[],
  fetcher: (ids: string[]) => Promise<T[]>,
  prefix: string,
  ttl: number = CACHE_TTL.DEFAULT
): Promise<T[]> {
  // Check cache for each item
  const cached: T[] = [];
  const toFetch: string[] = [];

  for (const id of items) {
    const item = cacheManager.get<T>(prefix, id);
    if (item) {
      cached.push(item);
    } else {
      toFetch.push(id);
    }
  }

  // Fetch missing items
  if (toFetch.length > 0) {
    const fetched = await fetcher(toFetch);
    
    // Cache fetched items
    for (const item of fetched) {
      cacheManager.set(prefix, item.id, item, ttl);
    }

    return [...cached, ...fetched];
  }

  return cached;
}

/**
 * Cache decorators for repositories
 */
export function cached(prefix: string, ttl: number = CACHE_TTL.DEFAULT) {
  return function(
    target: unknown,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = async function(...args: unknown[]) {
      // Create cache key from method name and arguments
      const identifier = args.length > 0 ? JSON.stringify(args) : propertyKey;
      
      return fetchWithCache(
        prefix,
        identifier,
        () => originalMethod.apply(this, args),
        ttl
      );
    };

    return descriptor;
  };
}

/**
 * Invalidate cache after mutation
 */
export function invalidateCache(prefix: string, identifier?: string) {
  return function(
    target: unknown,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = async function(...args: unknown[]) {
      const result = await originalMethod.apply(this, args);
      cacheManager.invalidate(prefix, identifier);
      return result;
    };

    return descriptor;
  };
}

export default cacheManager;
